import { ImagePlus } from 'lucide-react';
import { useState } from 'react';
import { artistProfilePath } from '@/lib/artistSlug';

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No image'));
      return;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const drawRoundedRect = (context, x, y, width, height, radius) => {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
};

const drawWrappedText = (context, text, x, y, maxWidth, lineHeight, maxLines, showEllipsis = true) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (context.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }
    currentLine = testLine;
  });

  if (currentLine) lines.push(currentLine);

  const visibleLines = lines.slice(0, maxLines);

  visibleLines.forEach((line, index) => {
    const suffix = showEllipsis && index === maxLines - 1 && lines.length > maxLines ? '...' : '';
    context.fillText(`${line}${suffix}`, x, y + index * lineHeight);
  });

  return visibleLines.length;
};

const downloadDataUrl = (dataUrl, filename) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getArtistPublicUrl = (artist) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.kalamanch.art';
  return `${siteUrl.replace(/\/$/, '')}${artistProfilePath(artist)}`;
};

const ArtistPosterButton = ({ artist, className = '' }) => {
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const createPoster = async () => {
    if (!artist) return;
    setBusy(true);
    setStatus('');
    const profileUrl = getArtistPublicUrl(artist);

    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const context = canvas.getContext('2d');

    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#FF6F00');
    gradient.addColorStop(0.45, '#E91E63');
    gradient.addColorStop(1, '#4A148C');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = 'rgba(255,255,255,0.13)';
    for (let i = 0; i < 18; i += 1) {
      context.beginPath();
      context.arc(100 + i * 72, 190 + (i % 2) * 36, 28, 0, Math.PI * 2);
      context.fill();
    }

    context.fillStyle = '#FFF8F0';
    drawRoundedRect(context, 84, 120, 912, 1110, 48);
    context.fill();

    context.fillStyle = '#111827';
    context.font = '700 34px Arial';
    context.fillText('KALAMANCH.ART', 132, 195);
    context.font = '600 24px Arial';
    context.fillStyle = '#0F766E';
    context.fillText('Post. Connect. Get Work.', 132, 235);

    const imageSize = 390;
    const imageX = 345;
    const imageY = 310;
    context.save();
    context.beginPath();
    context.arc(imageX + imageSize / 2, imageY + imageSize / 2, imageSize / 2, 0, Math.PI * 2);
    context.clip();

    try {
      const safeProfileImage = /res\.cloudinary\.com|kalamanch/i.test(artist.image_url || '') ? artist.image_url : '';
      const image = await loadImage(safeProfileImage);
      const scale = Math.max(imageSize / image.width, imageSize / image.height);
      const drawWidth = image.width * scale;
      const drawHeight = image.height * scale;
      context.drawImage(
        image,
        imageX + (imageSize - drawWidth) / 2,
        imageY + (imageSize - drawHeight) / 2,
        drawWidth,
        drawHeight
      );
    } catch (error) {
      context.fillStyle = '#FFE8D0';
      context.fillRect(imageX, imageY, imageSize, imageSize);
      context.fillStyle = '#FF6F00';
      context.font = '800 150px Arial';
      context.textAlign = 'center';
      context.fillText(artist.name?.charAt(0)?.toUpperCase() || 'A', imageX + imageSize / 2, imageY + 245);
      context.textAlign = 'left';
    }
    context.restore();

    context.strokeStyle = '#FFFFFF';
    context.lineWidth = 18;
    context.beginPath();
    context.arc(imageX + imageSize / 2, imageY + imageSize / 2, imageSize / 2 + 8, 0, Math.PI * 2);
    context.stroke();

    context.textAlign = 'center';
    context.fillStyle = '#111827';
    context.font = '800 64px Arial';
    const nameLines = drawWrappedText(context, artist.name || 'KalaManch Artist', 540, 780, 780, 66, 3, false);

    context.fillStyle = '#FF6F00';
    context.font = '700 34px Arial';
    const categoryY = 860 + nameLines * 66;
    const categoryLines = drawWrappedText(
      context,
      [artist.subcategory, artist.category].filter(Boolean).join(' | ') || 'Creative Artist',
      540,
      categoryY,
      760,
      42,
      3
    );

    context.fillStyle = '#4B5563';
    context.font = '500 30px Arial';
    const cityY = categoryY + categoryLines * 42 + 20;
    context.fillText(artist.city || 'India', 540, cityY);

    if (artist.is_verified) {
      context.fillStyle = '#16A34A';
      const verifiedY = cityY + 40;
      drawRoundedRect(context, 380, verifiedY, 320, 58, 29);
      context.fill();
      context.fillStyle = '#FFFFFF';
      context.font = '700 26px Arial';
      context.fillText('Verified on KalaManch', 540, verifiedY + 38);
    }

    context.fillStyle = '#111827';
    context.font = '700 28px Arial';
    context.fillText('Discover this artist profile', 540, 1150);
    context.fillStyle = '#4A148C';
    context.font = '700 24px Arial';
    drawWrappedText(context, profileUrl.replace(/^https?:\/\//, ''), 540, 1190, 760, 34, 2);

    context.textAlign = 'left';
    const dataUrl = canvas.toDataURL('image/png');
    const safeName = String(artist.name || 'artist').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadDataUrl(dataUrl, `kalamanch-${safeName || 'artist'}-poster.png`);
    setStatus('Poster downloaded');
    window.setTimeout(() => setStatus(''), 2500);
    setBusy(false);
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <button type="button" className="btn-outline inline-flex items-center justify-center gap-2 w-full sm:w-auto" onClick={createPoster} disabled={busy}>
        <ImagePlus className="h-4 w-4 shrink-0" aria-hidden="true" />{busy ? 'Creating Poster...' : 'Create Share Poster'}
      </button>
      {status && <span className="text-xs font-medium text-[#00796B]">{status}</span>}
    </div>
  );
};

export default ArtistPosterButton;
